import { Footer } from "~/components/Footer";
import { PageWrapper, SidebarLayout } from "~/components/SidebarLayout";
import { Navbar } from "~/components/Navbar/component";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { NextSeo } from "next-seo";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { Routes } from "~/routes";
import { Asset, AssetColumns } from "~/modules/assets/types";
import {
    FilterOperators,
    QueryResponse,
    SortDirections,
    Filter,
    FilterMetadata,
} from "~/modules/filters/types";
import { NEXTJS_REVALIDATE_TIMEOUT } from "~/server/consts";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { AssetClassPageSEO } from "~/modules/app/seo/AssetClassPageSEO";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import {
    LeagueTableGroupTypes,
    LeagueTableTabsByDistributionType,
} from "~/modules/league_tables/types";
import {
    ASSET_CLASS_DESCRIPTIONS,
    ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS,
    TREASURY_ASSETS_QUERY,
    ASSET_CLASS_PAGES_TO_FILTER,
} from "~/modules/asset_classes/consts";
import {
    ASSET_CLASS_IDS,
    DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
    DISTRIBUTION_TYPE_TOOLTIPS,
    MEASURE_IDS,
    SOCIAL_OG_IMAGE_URL,
    TOKENIZATION_TYPE_IDS,
} from "~/consts";
import { AssetClassIndividualPage } from "~/modules/asset_classes/pages/individual";
import {
    TokenTimeseriesGroupBy,
    TokenTimeseriesColumns,
} from "~/modules/token_timeseries/types";
import { Props as AggregateDisplayProps } from "~/components/AggregateDisplay";
import {
    NetFlowsData,
    queryNetFlows,
} from "~/modules/visualization/util/queryNetFlows";
import { AssetTableRowData } from "~/modules/assets/components/AssetTable/types";
import { calculatePercentChange } from "~/utils/calculatePercentChange";
import { getMeasureValue } from "~/utils/getMeasureValue";
import {
    queryTokenAggregateValueWith30D,
    queryTokenAggregateValueWith7D,
} from "~/modules/token_timeseries/api/queryTokenAggregateValue";
import { AggregateValueTypes } from "~/components/AggregateDisplay/types";
import { MetricTypes } from "~/modules/visualization/components/MetricTimeseriesChart/types";
import { getTimeseriesModes } from "~/modules/visualization/components/Timeseries/getTimeseriesModes";
import {
    SEVEN_DAYS_AGO,
    THIRTY_DAYS_AGO,
} from "~/modules/visualization/components/TimeSeriesChartV3/consts";
import { getTopTransfers } from "~/modules/transactions/api/getTopTransfers";
import { TopTransfersData } from "~/modules/transactions/components/TopTransfersCard/types";
import { TransactionColumns } from "~/modules/transactions/types";
import { AssetTimeseriesColumns } from "~/modules/asset_timeseries_v4/types";
import { ColumnMetadata } from "~/components/DataGrid/types";
import { APY_7_DAY_COLUMN } from "~/modules/assets/components/AssetTable/columns/Apy7Day";
import { HOLDING_ADDRESS_COUNT_COLUMN } from "~/modules/assets/components/AssetTable/columns/Holders";
import { NET_ASSET_VALUE_COLUMN } from "~/modules/assets/components/AssetTable/columns/NetAssetValue";
import { NETWORKS_COLUMN } from "~/modules/assets/components/AssetTable/columns/Networks";
import { ASSET_NAME_AND_TICKER_COLUMN_EXPANDABLE } from "~/modules/assets/components/AssetTable/columns/AssetName";
import { BRIDGED_TOKEN_VALUE_DOLLAR_COLUMN } from "~/modules/assets/components/AssetTable/columns/BridgedTokenValueDollar";
import { CONTRACT_ADDRESS_COLUMN } from "~/modules/assets/components/AssetTable/columns/ContractAddress";
import { MANAGEMENT_FEE_COLUMN } from "~/modules/assets/components/AssetTable/columns/ManagementFee";
import { PLATFORM_COLUMN } from "~/modules/assets/components/AssetTable/columns/Platform";
import { MANAGER_COLUMN } from "~/modules/assets/components/AssetTable/columns/Manager";
import {
    PRIMARY_MARKET_ALLOWED_INVESTORS_COLUMN,
    PRIMARY_MARKET_MINIMUM_SUBSCRIPTION_AMT_COLUMN,
    PRIMARY_MARKET_SUBSCRIPTION_FEE_COLUMN,
    PRIMARY_MARKET_REDEMPTION_FEE_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/PrimaryMarket";
import { TOTAL_PERFORMANCE_FEE_COLUMN } from "~/modules/assets/components/AssetTable/columns/TotalPerformanceFee";
import { INCORPORATION_COUNTRY_COLUMN } from "~/modules/assets/components/AssetTable/columns/IncorporationCountry";
import { LICENSE_OR_REGULATORY_FRAMEWORK } from "~/modules/assets/components/AssetTable/columns/RegulatoryFramework";
import { queryAssetFilterMeta, queryAssets } from "~/modules/assets/api/router";
import { TOKENIZATION_TYPE_COLUMN } from "~/modules/assets/components/AssetTable/columns/TokenizationType";
import { queryLeagueTablesByDistributionType } from "~/modules/league_tables/api/queryLeagueTablesByDistributionType";

// // // //

const SEO_TITLE = "Tokenized U.S. Treasury Funds";
const SEO_DESCRIPTION = ASSET_CLASS_DESCRIPTIONS.treasuries;

// // // //

const TREASURY_PRODUCT_TABLE_COLUMNS: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
>[] = [
    ASSET_NAME_AND_TICKER_COLUMN_EXPANDABLE,
    MANAGER_COLUMN,
    BRIDGED_TOKEN_VALUE_DOLLAR_COLUMN,
    NET_ASSET_VALUE_COLUMN,
    APY_7_DAY_COLUMN,
    HOLDING_ADDRESS_COUNT_COLUMN,
    TOKENIZATION_TYPE_COLUMN,
    PLATFORM_COLUMN,
    NETWORKS_COLUMN,
    PRIMARY_MARKET_ALLOWED_INVESTORS_COLUMN,
    // WEIGHTED_AVG_MATURITY_COLUMN,
    INCORPORATION_COUNTRY_COLUMN,
    LICENSE_OR_REGULATORY_FRAMEWORK,
    PRIMARY_MARKET_MINIMUM_SUBSCRIPTION_AMT_COLUMN,
    MANAGEMENT_FEE_COLUMN,
    PRIMARY_MARKET_SUBSCRIPTION_FEE_COLUMN,
    PRIMARY_MARKET_REDEMPTION_FEE_COLUMN,
    TOTAL_PERFORMANCE_FEE_COLUMN,
    CONTRACT_ADDRESS_COLUMN,
];

// // // //

// ENHANCEMENT - move this into the API
function get7DApyAggregateValues(products: Asset[]): {
    apy7DayWA: number;
    apy7DayPercentChange: number;
} {
    const validProducts = products.filter((product) => {
        const apy7Day = getMeasureValue(product, "apy_7_day");
        return apy7Day !== null;
    });

    const validMarketCapSum = validProducts.reduce(
        (total, product) =>
            total + (product.circulating_asset_value_dollar?.val ?? 0),
        0,
    );

    let apy7DayWASum = 0;
    let apy7DayWASum7d = 0;

    validProducts.forEach((product) => {
        const apy7Day = getMeasureValue(product, "apy_7_day");
        const marketCap = product.circulating_asset_value_dollar?.val ?? 0;

        if (apy7Day !== null && validMarketCapSum > 0) {
            const weight = marketCap / validMarketCapSum;
            apy7DayWASum += apy7Day.val * weight;
            apy7DayWASum7d += (apy7Day.val_7d ?? apy7Day.val) * weight;
        }
    });

    if (validProducts.length === 0) {
        return {
            apy7DayWA: 0,
            apy7DayPercentChange: 0,
        };
    }

    return {
        apy7DayWA: apy7DayWASum,
        apy7DayPercentChange: calculatePercentChange({
            initialValue: apy7DayWASum7d,
            finalValue: apy7DayWASum,
        }),
    };
}

// // // //

const TREASURY_TOP_TRANSFERS_FILTERS: Filter[] = [
    {
        field: TransactionColumns.assetClassID,
        operator: FilterOperators.Equals,
        value: ASSET_CLASS_IDS.TreasuryProduct,
    },
];

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation is enabled and a new request comes in
// NOTE - this will have to be changed to hit api.rwa.xyz when the API has been updated
export async function getStaticProps() {
    // Base filters for all treasury queries
    const baseFilters: Filter[] =
        ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS.treasuries;

    // Make all requests in parallel + destructure responses
    const [
        listQueryResponse,
        filterMetadata,
        leagueTableTabs,
        netFlows,
        distributedAggregates,
        representedAggregates,
        holdersData,
        // Top Transfers
        transactions_7d_under100k,
        transactions_7d_under1m,
        transactions_7d_under10m,
        transactions_7d_under_100m,
        transactions_30d_under100k,
        transactions_30d_under1m,
        transactions_30d_under10m,
        transactions_30d_under_100m,
    ] = await Promise.all([
        queryAssets({
            query: TREASURY_ASSETS_QUERY,
        }),
        queryAssetFilterMeta({
            filter: ASSET_CLASS_PAGES_TO_FILTER.treasuries,
        }),
        queryLeagueTablesByDistributionType({
            name: "asset-class--treasuries",
            filter: {
                operator: FilterOperators.And,
                filters: baseFilters,
            },
            groups: [
                LeagueTableGroupTypes.platforms,
                LeagueTableGroupTypes.managers,
                LeagueTableGroupTypes.networks,
                LeagueTableGroupTypes.jurisdictionCountries,
            ],
        }),
        queryNetFlows({
            name: "asset-class--treasuries",
            filters: [
                {
                    field: TokenTimeseriesColumns.assetClassID,
                    operator: FilterOperators.Equals,
                    value: ASSET_CLASS_IDS.TreasuryProduct,
                },
            ],
            groupBy: TokenTimeseriesGroupBy.asset,
            limit: 8,
        }),
        // 1. Distributed Asset Value
        queryTokenAggregateValueWith30D({
            name: "asset-class--treasuries--distributed-asset-value",
            measureID: MEASURE_IDS.BridgedTokenValueDollar,
            filters: [
                ...baseFilters,
                {
                    field: TokenTimeseriesColumns.tokenizationTypeID,
                    operator: FilterOperators.Equals,
                    value: TOKENIZATION_TYPE_IDS.OnChainDistributed,
                },
            ],
        }),
        // 2. Represented Asset Value
        queryTokenAggregateValueWith30D({
            name: "asset-class--treasuries--represented-asset-value",
            measureID: MEASURE_IDS.BridgedTokenValueDollar,
            filters: [
                ...baseFilters,
                {
                    field: TokenTimeseriesColumns.tokenizationTypeID,
                    operator: FilterOperators.Equals,
                    value: TOKENIZATION_TYPE_IDS.OnChainRepresented,
                },
            ],
        }),
        queryTokenAggregateValueWith7D({
            measureID: MEASURE_IDS.DailyNumberOfHolders,
            filters: baseFilters,
        }),
        // // // //
        // Top Transfers
        // 7-day transactions
        getTopTransfers({
            name: "asset-class--treasuries--7d-under-100k",
            filters: TREASURY_TOP_TRANSFERS_FILTERS,
            max: 100_000,
            timestamp: SEVEN_DAYS_AGO,
        }),
        getTopTransfers({
            name: "asset-class--treasuries--7d-under-1m",
            filters: TREASURY_TOP_TRANSFERS_FILTERS,
            max: 1_000_000,
            timestamp: SEVEN_DAYS_AGO,
        }),
        getTopTransfers({
            name: "asset-class--treasuries--7d-under-10m",
            filters: TREASURY_TOP_TRANSFERS_FILTERS,
            max: 10_000_000,
            timestamp: SEVEN_DAYS_AGO,
        }),
        getTopTransfers({
            name: "asset-class--treasuries--7d-under-100m",
            filters: TREASURY_TOP_TRANSFERS_FILTERS,
            timestamp: SEVEN_DAYS_AGO,
        }),
        // // // //
        // 30-day transactions
        getTopTransfers({
            name: "asset-class--treasuries--30d-under-100k",
            filters: TREASURY_TOP_TRANSFERS_FILTERS,
            max: 100_000,
            timestamp: THIRTY_DAYS_AGO,
        }),
        getTopTransfers({
            name: "asset-class--treasuries--30d-under-1m",
            filters: TREASURY_TOP_TRANSFERS_FILTERS,
            max: 1_000_000,
            timestamp: THIRTY_DAYS_AGO,
        }),
        getTopTransfers({
            name: "asset-class--treasuries--30d-under-10m",
            filters: TREASURY_TOP_TRANSFERS_FILTERS,
            max: 10_000_000,
            timestamp: THIRTY_DAYS_AGO,
        }),
        getTopTransfers({
            name: "asset-class--treasuries--30d-under-100m",
            filters: TREASURY_TOP_TRANSFERS_FILTERS,
            timestamp: THIRTY_DAYS_AGO,
        }),
    ]);

    // Calculate APY client-side (requires weighted average)
    const aggregateValues = get7DApyAggregateValues(listQueryResponse.results);

    const aggregates: Array<AggregateDisplayProps> = [
        {
            label: "Distributed Value",
            value: distributedAggregates.value,
            type: AggregateValueTypes.dollarCompact,
            tooltip: DISTRIBUTION_TYPE_TOOLTIPS.distributed,
            tooltipLinkTo: DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
            percentChange: {
                value: distributedAggregates.change30D,
                interval: "30d",
            },
        },
        ...(representedAggregates.value > 0
            ? [
                  {
                      label: "Represented Value",
                      value: representedAggregates.value,
                      type: AggregateValueTypes.dollarCompact,
                      tooltip: DISTRIBUTION_TYPE_TOOLTIPS.represented,
                      tooltipLinkTo:
                          DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
                      percentChange: {
                          value: representedAggregates.change30D,
                          interval: "30d",
                      },
                  } satisfies AggregateDisplayProps,
              ]
            : []),
        {
            label: "7D APY",
            value: aggregateValues.apy7DayWA / 100,
            type: AggregateValueTypes.percent,
            tooltip:
                "Weighted average 7-day APY of all tokenized treasury fund tokens in circulation.",
            percentChange: {
                value: aggregateValues.apy7DayPercentChange,
                interval: "7d",
            },
        },
        {
            label: "Assets",
            value: listQueryResponse.results.length,
            type: AggregateValueTypes.count,
        },
        {
            label: "Holders",
            value: holdersData.value,
            type: AggregateValueTypes.count,
            tooltip:
                "The number of addresses that hold tokenized treasury fund tokens in circulation.",
            percentChange: {
                value: holdersData.change7D,
                interval: "7d",
            },
        },
    ];

    // Trim down FilterMetadata options to only include the ones we need
    const trimmedFilterMetadata = filterMetadata.filter((each) => {
        return [AssetColumns.networkIDs, AssetColumns.protocolIDs].includes(
            each.field as AssetColumns,
        );
    });

    return {
        props: {
            listQueryResponse,
            filterMetadata: trimmedFilterMetadata,
            leagueTableTabs,
            netFlows,
            aggregates,
            topTransfers: {
                seven_day: {
                    under_100k: transactions_7d_under100k,
                    under_1m: transactions_7d_under1m,
                    under_10m: transactions_7d_under10m,
                    under_100m: transactions_7d_under_100m,
                },
                thirty_day: {
                    under_100k: transactions_30d_under100k,
                    under_1m: transactions_30d_under1m,
                    under_10m: transactions_30d_under10m,
                    under_100m: transactions_30d_under_100m,
                },
            },
        },
        // Next.js will re-generate this page based on NEXTJS_REVALIDATE_TIMEOUT const
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
}

export default function TreasuriesPage(props: {
    aggregates: Array<AggregateDisplayProps>;
    listQueryResponse: QueryResponse<Asset>;
    filterMetadata: FilterMetadata[];
    leagueTableTabs: LeagueTableTabsByDistributionType;
    netFlows: NetFlowsData;
    topTransfers: TopTransfersData;
}) {
    const {
        aggregates,
        listQueryResponse,
        filterMetadata,
        leagueTableTabs,
        netFlows,
        topTransfers,
    } = props;

    const modes = getTimeseriesModes({
        analyticsKey: "treasury",
        defaultGroupBy: TokenTimeseriesGroupBy.asset,
        filters: ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS.treasuries,
        assetTimeseriesFilters: [
            {
                field: AssetTimeseriesColumns.assetClassID,
                operator: FilterOperators.Equals,
                value: ASSET_CLASS_IDS.TreasuryProduct,
            },
            {
                field: AssetTimeseriesColumns.isInvestable,
                operator: FilterOperators.Equals,
                value: true,
            },
        ],
        metrics: [
            MetricTypes.TotalValue,
            MetricTypes.AssetHolders,
            MetricTypes.ActiveAddresses,
            MetricTypes.TransferVolume,
            MetricTypes.MintBurnVolume,
            MetricTypes.Apy7Day,
        ],
    });

    return (
        <PageTrack route={Routes.treasuries}>
            <NextSeo
                title={SEO_TITLE}
                description={SEO_DESCRIPTION}
                canonical={addBaseUrl(Routes.treasuries)}
                openGraph={{
                    type: "website",
                    url: addBaseUrl(Routes.treasuries),
                    title: SEO_TITLE,
                    site_name: "RWA.xyz",
                    description: SEO_DESCRIPTION,
                    images: [
                        {
                            url: SOCIAL_OG_IMAGE_URL,
                            width: 4000,
                            height: 2300,
                            alt: "rwa.xyz",
                        },
                    ],
                }}
                additionalMetaTags={[
                    {
                        name: "keywords",
                        content:
                            "tokenized treasuries, treasury bonds, T-bills, yield to maturity, tokenized government debt, BlackRock BUIDL, OUSG, USDY, real world assets",
                    },
                ]}
            />
            <AssetClassPageSEO
                route={Routes.treasuries}
                description={SEO_DESCRIPTION}
            />
            <BreadcrumbSEO
                items={[
                    {
                        name: SEO_TITLE,
                        href: Routes.treasuries,
                    },
                ]}
            />
            <UserFetcher>
                {({ user }) => <Navbar route={Routes.treasuries} user={user} />}
            </UserFetcher>
            <SidebarLayout pathname={Routes.treasuries}>
                <PageWrapper>
                    <AssetClassIndividualPage
                        listQueryResponse={listQueryResponse}
                        title={SEO_TITLE}
                        subtitle={SEO_DESCRIPTION}
                        netFlows={{
                            title: "Treasury Fund Net Flows",
                            data: netFlows,
                        }}
                        assetsQuery={TREASURY_ASSETS_QUERY}
                        header={{
                            label: "Tokenized U.S. Treasury Funds",
                            loading: false,
                            value: `${listQueryResponse.results.length.toLocaleString()} Total`,
                            // description: "Expand rows to view different networks supported by each private equity product.",
                        }}
                        aggregates={aggregates}
                        timeseriesChart={{
                            title: "Treasury Fund Metrics",
                            modes,
                            distributionSelect: {
                                distributed: {
                                    enabled: true,
                                },
                                represented: {
                                    enabled: true,
                                },
                                stablecoins: {
                                    enabled: false,
                                    active: false,
                                },
                            },
                            columnSpanConfig: {
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 12,
                                xxl: 12,
                            },
                        }}
                        pieChart={{
                            title: "Treasury Funds Value",
                            leagueTableData: leagueTableTabs,
                            defaultGroupBy: LeagueTableGroupTypes.networks,
                            allowedGroupByValues: [
                                LeagueTableGroupTypes.platforms,
                                LeagueTableGroupTypes.managers,
                                LeagueTableGroupTypes.networks,
                                LeagueTableGroupTypes.jurisdictionCountries,
                            ],
                            colSpans: {
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            },
                        }}
                        topTransfers={{
                            title: "Latest Transactions",
                            topTransfers: topTransfers,
                        }}
                        leagueTable={{
                            leagueTableData: leagueTableTabs,
                            analyticsTableName:
                                "treasury-products-league-table",
                            title: "Treasury Fund League Table",
                            colSpans: {
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            },
                        }}
                        table={{
                            filterMetadata,
                            columns: TREASURY_PRODUCT_TABLE_COLUMNS,
                            analyticsTableName: "treasury_products",
                            exportFilename: "treasuries-assets",
                            sortQuery: {
                                field: AssetColumns.bridgedTokenValueDollar,
                                direction: SortDirections.desc,
                            },
                        }}
                    />
                    <Footer />
                </PageWrapper>
            </SidebarLayout>
        </PageTrack>
    );
}
